import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { createApiUrl } from '../utils/apiConfig';
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
  Elements
} from '@stripe/react-stripe-js';
import axios from 'axios';
import './PaymentUpdate.css';

// Get the Stripe key from the server
const getStripeKey = async () => {
  try {
    const response = await axios.get(createApiUrl('/api/stripe-key'));
    return response.data.publishableKey;
  } catch (error) {
    console.error('Error fetching Stripe key:', error);
    return null;
  }
};

const PaymentUpdateForm = () => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setProcessing(true);
    setError(null);

    if (!stripe || !elements) {
      return;
    }

    try {
      const response = await axios.post(createApiUrl('/api/handle-payment-failure'));
      const { clientSecret } = response.data;

      const { error: confirmError } = await stripe.confirmCardSetup(
        clientSecret,
        {
          payment_method: {
            card: elements.getElement(CardNumberElement),
          },
        }
      );

      if (confirmError) {
        setError(confirmError.message);
      } else {
        setSuccess(true);
        setTimeout(() => {
          navigate('/teacher-dashboard?message=Payment details updated successfully');
        }, 3000);
      }
    } catch (err) {
      setError('An error occurred while updating payment details.');
      console.error('Error:', err);
    } finally {
      setProcessing(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        color: '#32325d',
        fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
        fontSmoothing: 'antialiased',
        fontSize: '16px',
        '::placeholder': {
          color: '#aab7c4'
        }
      },
      invalid: {
        color: '#fa755a',
        iconColor: '#fa755a'
      }
    }
  };

  return (
    <div className="container">
      <div className="row justify-content-center">
        <div className="col-md-6">
          <h1 className="text-center">Update Payment Details</h1>
          
          {success && (
            <div className="alert alert-success" role="alert">
              Payment details updated successfully. Redirecting to dashboard...
            </div>
          )}

          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="payment-form">
            <div className="form-group">
              <label htmlFor="card-number">Card Number</label>
              <div className="stripe-element">
                <CardNumberElement
                  id="card-number"
                  options={cardElementOptions}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="card-expiry">Expiration Date</label>
              <div className="stripe-element">
                <CardExpiryElement
                  id="card-expiry"
                  options={cardElementOptions}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="card-cvc">CVC</label>
              <div className="stripe-element">
                <CardCvcElement
                  id="card-cvc"
                  options={cardElementOptions}
                />
              </div>
            </div>

            <div className="form-group">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={!stripe || processing}
              >
                {processing ? 'Processing...' : 'Update Payment'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const PaymentUpdate = () => {
  const [stripePromise, setStripePromise] = useState(null);

  useEffect(() => {
    const initializeStripe = async () => {
      const key = await getStripeKey();
      if (key) {
        setStripePromise(loadStripe(key));
      }
    };
    initializeStripe();
  }, []);

  if (!stripePromise) {
    return <div>Loading payment form...</div>;
  }

  return (
    <div className="payment-update-container">
      <Elements stripe={stripePromise}>
        <PaymentUpdateForm />
      </Elements>
    </div>
  );
};

export default PaymentUpdate; 